import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Minimal accessible pagination control. Calls onPageChange(p).
 * Shows: « Prev  1  ...  current-1 current current+1  ...  last  Next »
 */
export default function Pagination({ page, totalPages, onPageChange }) {
  if (!totalPages || totalPages < 2) return null;
  const go = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onPageChange(p);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Build page number window
  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  const list = Array.from(pages).filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < list.length; i++) {
    if (i > 0 && list[i] - list[i - 1] > 1) out.push("…");
    out.push(list[i]);
  }

  return (
    <nav data-testid="pagination" aria-label="Pagination" className="mt-8 flex items-center justify-center gap-1 flex-wrap">
      <button
        data-testid="pagination-prev"
        onClick={() => go(page - 1)}
        disabled={page === 1}
        aria-label="Go to previous page"
        className="inline-flex items-center justify-center min-h-11 min-w-11 px-3 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
        <span className="hidden sm:inline ml-1">Prev</span>
      </button>
      {out.map((p, i) =>
        p === "…" ? (
          <span key={`e${i}`} className="px-2 text-slate-400" aria-hidden="true">…</span>
        ) : (
          <button
            key={p}
            data-testid={`pagination-page-${p}`}
            onClick={() => go(p)}
            aria-current={p === page ? "page" : undefined}
            aria-label={`Go to page ${p}`}
            className={`inline-flex items-center justify-center min-h-11 min-w-11 px-3 rounded-md text-sm font-semibold transition-colors ${
              p === page
                ? "bg-orange-600 text-white"
                : "border border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        data-testid="pagination-next"
        onClick={() => go(page + 1)}
        disabled={page === totalPages}
        aria-label="Go to next page"
        className="inline-flex items-center justify-center min-h-11 min-w-11 px-3 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <span className="hidden sm:inline mr-1">Next</span>
        <ChevronRight className="w-4 h-4" aria-hidden="true" />
      </button>
    </nav>
  );
}
